const internals = {
    transientStatuses: new Set([408, 429, 500, 502, 503, 504])
};

module.exports = {
    createProfileImageLoader,
    profileImageLoader: null
};

// Browser APIs are resolved when an attempt starts, so importing this module is safe in SSR.
function createProfileImageLoader(options = {}) {

    const records = new Map();
    const random = options.random || Math.random;
    const retentionMs = 5 * 60 * 1000;
    const maxInactive = options.maxInactive ?? 200;
    const maxInactiveBytes = options.maxInactiveBytes ?? 32 * 1024 * 1024;
    let active = 0;
    let timer;
    let pumpScheduled = false;
    let disposed = false;
    let consecutiveFailures = 0;
    let cooldownUntil = 0;
    let probeActive = false;

    const publish = (record, status, src = null) => {

        record.snapshot = { status, src };
        record.listeners.forEach((listener) => listener(record.snapshot));
    };

    const releaseBlob = (record) => {

        if (record.snapshot.src) {
            URL.revokeObjectURL(record.snapshot.src);
        }

        record.bytes = 0;
    };

    const prune = () => {

        const inactive = [...records.values()]
            .filter((record) => !record.listeners.size && !record.controller)
            .sort((a, b) => a.touchedAt - b.touchedAt);
        let bytes = inactive.reduce((total, record) => total + record.bytes, 0);
        let count = inactive.length;

        inactive.forEach((record) => {

            if (Date.now() - record.touchedAt >= retentionMs || count > maxInactive || bytes > maxInactiveBytes) {
                bytes -= record.bytes;
                --count;
                releaseBlob(record);
                records.delete(record.url);
            }
        });
    };

    const schedulePump = () => {

        if (!pumpScheduled && !disposed) {
            pumpScheduled = true;
            Promise.resolve().then(() => {

                pumpScheduled = false;
                pump();
            });
        }
    };

    const openCircuit = () => {

        cooldownUntil = Date.now() + 10000;
    };

    const attempt = async (record, isProbe) => {

        ++active;
        ++record.attempts;
        const controller = new AbortController();
        record.controller = controller;
        let deadlineExpired = false;
        const deadline = setTimeout(() => {

            if (!controller.signal.aborted) {
                deadlineExpired = true;
                controller.abort();
            }
        }, options.requestTimeoutMs ?? 15000);

        try {
            const fetchImage = options.fetch || globalThis.fetch;
            const response = await fetchImage(record.url, { signal: controller.signal, credentials: 'omit', mode: 'cors' });

            if (!response.ok) {
                const error = new Error('Profile image request failed');
                error.transient = internals.transientStatuses.has(response.status);
                error.retryAfter = response.headers.get('Retry-After');
                await response.body?.cancel();
                throw error;
            }

            const blob = await response.blob();

            if (controller.signal.aborted) {
                throw new Error('Profile image request aborted');
            }

            record.bytes = blob.size;
            record.touchedAt = Date.now();
            publish(record, 'loaded', URL.createObjectURL(blob));

            // An older in-flight success must not bypass a circuit opened by other requests.
            if (!cooldownUntil || isProbe) {
                consecutiveFailures = 0;
                cooldownUntil = 0;
                probeActive = false;
            }
        }
        catch (error) {
            if (disposed || (controller.signal.aborted && !deadlineExpired)) {
                --record.attempts;
                return;
            }

            const transient = deadlineExpired || error.transient !== false;
            if (transient) {
                ++consecutiveFailures;
                if (isProbe || (!cooldownUntil && consecutiveFailures >= 4)) {
                    openCircuit();
                }
            }
            else if (!cooldownUntil || isProbe) {
                consecutiveFailures = 0;
                cooldownUntil = 0;
                probeActive = false;
            }

            record.touchedAt = Date.now();
            if (transient && record.attempts < 3) {
                const backoff = 2000 * (2 ** (record.attempts - 1));
                record.readyAt = Date.now() + Math.max(backoff, internals.retryAfterMs(error.retryAfter)) + random() * backoff;
            }
            else {
                publish(record, 'error');
            }
        }
        finally {
            clearTimeout(deadline);
            record.controller = null;
            --active;
            if (isProbe) {
                probeActive = false;
            }

            schedulePump();
        }
    };

    const pump = () => {

        clearTimeout(timer);
        if (disposed) {
            return;
        }

        prune();
        const pending = [...records.values()]
            .filter((record) => record.listeners.size && !record.controller && record.snapshot.status === 'loading')
            .sort((a, b) => b.priority - a.priority || a.readyAt - b.readyAt);

        for (const record of pending) {
            if (active >= 4 || probeActive || (cooldownUntil && Date.now() < cooldownUntil)) {
                break;
            }

            if (record.readyAt > Date.now()) {
                continue;
            }

            const isProbe = Boolean(cooldownUntil);
            if (isProbe) {
                probeActive = true;
            }

            attempt(record, isProbe);
        }

        let nextWake = Infinity;
        if (active < 4 && !probeActive) {
            pending.forEach((record) => {

                if (!record.controller) {
                    nextWake = Math.min(nextWake, Math.max(record.readyAt, cooldownUntil || 0));
                }
            });
        }

        records.forEach((record) => {

            if (!record.listeners.size && !record.controller) {
                nextWake = Math.min(nextWake, record.touchedAt + retentionMs);
            }
        });

        if (Number.isFinite(nextWake)) {
            // setTimeout wraps delays above a signed 32-bit integer.
            timer = setTimeout(pump, Math.min(2147483647, Math.max(1, nextWake - Date.now())));
        }
    };

    return {
        subscribe(url, listener, { priority = 0 } = {}) {

            if (disposed) {
                throw new Error('Profile image loader is disposed');
            }

            prune();
            let record = records.get(url);
            if (!record) {
                record = {
                    url,
                    listeners: new Set(),
                    snapshot: { status: 'loading', src: null },
                    attempts: 0,
                    readyAt: Date.now(),
                    touchedAt: Date.now(),
                    bytes: 0,
                    priority,
                    controller: null
                };
                records.set(url, record);
            }

            record.priority = Math.max(record.priority, priority);
            record.listeners.add(listener);
            listener(record.snapshot);
            pump();
            let subscribed = true;

            return () => {

                if (!subscribed) {
                    return;
                }

                subscribed = false;
                record.listeners.delete(listener);
                if (!record.listeners.size) {
                    record.touchedAt = Date.now();
                    record.controller?.abort();
                }

                pump();
            };
        },

        invalidate(url) {

            const record = records.get(url);
            if (record?.snapshot.status === 'loaded') {
                releaseBlob(record);
                record.touchedAt = Date.now();
                publish(record, 'error');
                pump();
            }
        },

        dispose() {

            disposed = true;
            clearTimeout(timer);
            records.forEach((record) => {

                record.controller?.abort();
                releaseBlob(record);
            });
            records.clear();
        }
    };
}

internals.retryAfterMs = (value) => {

    if (!value) {
        return 0;
    }

    const seconds = /^\d+(?:\.\d+)?$/.test(value) ? Number(value) : NaN;
    const delay = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(value) - Date.now();
    return Number.isFinite(delay) ? Math.max(0, delay) : 0;
};

module.exports.profileImageLoader = createProfileImageLoader();
